export type MakeCurrentRevisionFailure = 'MAKE_CURRENT' | 'RECORD_REVISION';

export interface MakeCurrentRevisionResult {
	ok: boolean;
	failedStage?: MakeCurrentRevisionFailure;
}

export interface PreviousCurrentItems {
	drawings: any[];
	specs: any[];
}

const definedEntries = (values: { [key: string]: any }) =>
	Object.keys(values).reduce((result, key) => {
		const value = values[key];
		if (value !== undefined && value !== null && value !== '') result[key] = value;
		return result;
	}, {} as { [key: string]: any });

export const buildPreviousCurrentItems = ({
	drawings = [],
	specs = [],
}: {
	drawings?: any[];
	specs?: any[];
}): PreviousCurrentItems => ({
	drawings: (Array.isArray(drawings) ? drawings : []).flatMap((group: any) => {
		const rows = Array.isArray(group?.pages) ? group.pages : [group];
		return rows
			.filter((row: any) => row && row.isCurrentDrawing !== false)
			.map((row: any) =>
				definedEntries({
					id: row.id,
					documentId: row.documentId,
					identifier: row.identifier || row.pageValue || row.pageNumber || row.name,
					pageValue: row.pageValue,
					title: row.title || row.documentName || group?.name,
					documentVersion: row.documentVersion || row.version,
					documentVersionNumber: row.documentVersionNumber || row.versionNumber,
					pageNumber: row.pageNumber,
					version: row.version,
					versionNumber: row.versionNumber,
					fileName: row.fileName,
					projectArea: row.projectArea || group?.projectArea,
					isCurrentDrawing: row.isCurrentDrawing,
				}),
			);
	}),
	specs: (Array.isArray(specs) ? specs : [])
		.filter((row: any) => row && row.isCurrentSpec !== false)
		.map((row: any) =>
			definedEntries({
				id: row.id,
				specificationId: row.specificationId,
				identifier:
					row.identifier ||
					row.specSheetNumber ||
					row.specificationNumber ||
					row.originalIdentifier,
				title: row.title || row.specificationName || row.name,
				originalIdentifier: row.originalIdentifier || row.specificationNumber,
				specificationNumber: row.specificationNumber,
				documentVersion: row.documentVersion || row.specificationVersion || row.version,
				documentVersionNumber: row.documentVersionNumber || row.versionNumber,
				specSheetNumber: row.specSheetNumber,
				version: row.version || row.specificationVersion,
				versionNumber: row.versionNumber,
				fileName: row.fileName,
				projectArea: row.projectArea,
				isCurrentSpec: row.isCurrentSpec,
			}),
		),
});

export const runMakeCurrentRevisionWorkflow = async ({
	makeCurrent,
	recordRevision,
}: {
	makeCurrent: () => Promise<boolean>;
	recordRevision: () => Promise<boolean>;
}): Promise<MakeCurrentRevisionResult> => {
	const madeCurrent = await makeCurrent();
	if (!madeCurrent) return { ok: false, failedStage: 'MAKE_CURRENT' };

	const revisionRecorded = await recordRevision();
	if (!revisionRecorded) return { ok: false, failedStage: 'RECORD_REVISION' };

	return { ok: true };
};

export const getMakeCurrentRevisionError = (
	result: MakeCurrentRevisionResult,
	documentLabel: string,
) =>
	result.failedStage === 'RECORD_REVISION'
		? `The ${documentLabel} is current, but its bid-leveling version could not be created. Please try again to finish the update.`
		: `We could not make this ${documentLabel} current. No bid-leveling version was created. Please try again.`;
